import React, { useState, ReactElement, useRef } from 'react';
import { css } from 'glamor';
import styled from '@emotion/styled';
import Button from '../../../atoms/buttons/Button';
import { DashlineSeparator, VerticalSeparator } from '../styledElements';
import { Box } from '@material-ui/core';
import JoinSkipButton from '../JoinSkipButton';
import { handleJoinVysionEvent } from '../../../utils/vysion/join';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { JoinClickVysionEventsEnum } from '../../../utils/vysion/join/click';
import breakpoints from '../../../theme/material/breakpoints';
import { ICustomCSSProperties } from 'atoms/types';
import { FONT_FAMILIES } from 'theme/material/constants';

const CarouselContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 490,
  [breakpoints.down('sm')]: {
    minHeight: 'unset',
  },
});

const CarouselStyled = styled('div')({
  display: 'inline-flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
});

const StyledList = styled('ul')({
  margin: 0,
  paddingInlineStart: 0,
  listStyleType: 'none',
  padding: '0px 0px',
  [breakpoints.down('sm')]: {
    padding: '0px 20px',
  },
});

const ButtonsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: '20px',
});

const ContentList = styled('ul')({
  listStyleType: 'disc',
  marginTop: 20,
  paddingInlineStart: '20px',
  fontFamily: FONT_FAMILIES.primary,
  minHeight: 184,
});

const ContentListItem = styled('li')({
  textAlign: 'left',
  paddingBottom: 10,
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '-0.3px',
  fontStyle: 'normal',
  fontFamily: FONT_FAMILIES.primary,
});

const slideStyle = {
  marginRight: 'auto',
  marginLeft: 'auto',
  listStyleType: 'none',
  textAlign: 'center',
  display: 'none',
};

const slideStyleActive = {
  display: 'block',
};

const StyledIndicators = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
};

const StyledIndicatorsList = styled('li')({
  '&:nth-of-type(n + 2)': {
    marginLeft: 9,
  },
});

const indicatorStyle = {
  display: 'block',
  width: 10,
  height: 10,
  backgroundColor: '#FFFFFF',
  cursor: 'pointer',
  transition: 'opacity .15s cubic-bezier(.4, 0, 1, 1)',
  borderRadius: '50%',
  border: 'solid 1px #2B8FD7',
};

const indicatorStyleActive = {
  backgroundColor: '#2B8FD7',
};

const textStyle: ICustomCSSProperties = {
  textAlign: 'center',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '16px',
};

const TitleContainer = styled('h3')({
  fontStyle: 'normal',
  fontWeight: 700,
  color: '#2B8FD7',
  fontSize: 22,
  lineHeight: '27px',
  letterSpacing: '-0.3px',
  margin: '0px 0px 20px 0px',
});

const SubTitle = styled('h4')({
  textAlign: 'center',
  fontSize: 18,
  lineHeight: '20px',
  letterSpacing: '-0.3px',
  fontStyle: 'normal',
  fontWeight: 600,
  color: '#212121',
  margin: '20px 0px 0px 0px',
});

const ButtonCustomStyle = {
  fontWeight: 400,
  textDecoration: 'none',
  color: '#8392A7',
  lineHeight: '17px',
};

interface CarouselSlideProps {
  index: any;
  activeIdx: number;
  slide: {
    icon: string;
    iconStyle: null;
    title: string;
    content: any[];
    subTitle: string;
  };
}

const CarouselIndicator = (
  idx: React.Key | null | undefined,
  activeIdx: number,
  onClick: React.MouseEventHandler<HTMLAnchorElement> | undefined,
  clickable: boolean
) => {
  return (
    <StyledIndicatorsList key={idx}>
      <a
        className={
          idx == activeIdx ? `${css({ ...indicatorStyle, ...indicatorStyleActive })}` : `${css(indicatorStyle)}`
        }
        style={{ cursor: clickable ? 'pointer' : 'default' }}
        onClick={clickable ? onClick : undefined}
      />
    </StyledIndicatorsList>
  );
};

const CarouselSlide = (props: CarouselSlideProps) => {
  const { index, activeIdx, slide } = props;
  const imgSrc = slide.icon;

  return (
    <>
      <li
        key={Math.floor(Math.random() * 999)}
        className={index == activeIdx ? `${css({ ...slideStyle, ...slideStyleActive })}` : `${css(slideStyle)}`}
      >
        <TitleContainer>{slide.title}</TitleContainer>
        {imgSrc && imgSrc}

        <ContentList>
          {slide.content.map((item: any, index: React.Key | null | undefined) => {
            return <ContentListItem key={index}>{item}</ContentListItem>;
          })}
        </ContentList>
        <SubTitle>{slide.subTitle}</SubTitle>
      </li>
    </>
  );
};

interface OfferTutorialSliderProps {
  slidesData: any;
  clickableIndicators: boolean;
  onClose: any;
  customButtonContainerStyle: any;
}

const OfferTutorialSlider = ({
  slidesData = {},
  clickableIndicators = false,
  onClose = null,
  customButtonContainerStyle = {},
}: OfferTutorialSliderProps): ReactElement => {
  const [activeIndex, setActiveIndex] = useState(0);
  const closeBtnRef = useRef(null);

  const goToSlide = (idx: React.SetStateAction<number>) => {
    setActiveIndex(idx);
  };

  const goToNextSlide = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    let idx = activeIndex;
    const slidesLength = slidesData.length - 1;

    if (idx === slidesLength) {
      return false;
    }

    ++idx;
    setActiveIndex(idx);
  };

  const handlePrevClick = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    let idx = activeIndex;

    --idx;
    setActiveIndex(idx);
  };

  const handleFinishAndNextClick = (e: { preventDefault: () => void }) => {
    if (isLastSlide) {
      onClose(closeBtnRef);
      handleJoinVysionEvent({
        action: VYSION_ACTIONS.CLICK,
        event: JoinClickVysionEventsEnum.CLICK_JOIN_FINISH,
      });
      return;
    }

    closeBtnRef?.current?.blur();
    goToNextSlide(e);
  };

  const handleSkipAndFinish = () => {
    onClose(closeBtnRef);
    handleJoinVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: JoinClickVysionEventsEnum.CLICK_JOIN_SKIP_AND_FINISH,
    });
  };

  const isLastSlide = activeIndex === slidesData.length - 1;

  return (
    <CarouselContainer>
      <CarouselStyled>
        <StyledList>
          {slidesData.map((slide: any, index: any) => CarouselSlide({ index, activeIdx: activeIndex, slide }))}
        </StyledList>
        <Box width={'100%'}>
          <DashlineSeparator />
        </Box>
        <StyledList className={`${css(StyledIndicators)}`}>
          {slidesData.map((_slide: any, index: any) =>
            CarouselIndicator(index, activeIndex, () => goToSlide(index), clickableIndicators)
          )}
        </StyledList>
        <ButtonsContainer style={customButtonContainerStyle}>
          <Button
            buttonRef={closeBtnRef}
            id="carouselNextBtn"
            buttonType="primaryRed"
            onClick={handleFinishAndNextClick}
            type="submit"
            data-test-id="carousel-next-btn"
            customStyle={{ padding: '12px 40px', marginBottom: '12px', height: '100%' }}
            isDefault={true}
            buttonTextCustomStyle={textStyle}
          >
            {isLastSlide ? 'Finish' : 'Next'}
          </Button>
          <VerticalSeparator>
            <JoinSkipButton
              label="Back"
              onClick={(e: { preventDefault: () => void }) => handlePrevClick(e)}
              customStyle={{ ...ButtonCustomStyle, display: activeIndex === 0 ? 'none' : 'inherit' }}
            />

            {activeIndex === 0 && (
              <>
                <JoinSkipButton customStyle={ButtonCustomStyle} label="Skip and Finish" onClick={handleSkipAndFinish} />
              </>
            )}
          </VerticalSeparator>
        </ButtonsContainer>
      </CarouselStyled>
    </CarouselContainer>
  );
};

export default OfferTutorialSlider;
